import { useState, useRef, useEffect, useCallback } from "react";
import { SWIPE_THRESHOLD_PX } from "../../config/constants";

/**
 * Full-screen image lightbox with keyboard navigation and touch swipe support.
 *
 * @param {Object}   props
 * @param {string[]} props.photos       - Array of image URLs.
 * @param {number}   props.initialIndex - Index of the first photo to display.
 * @param {boolean}  [props.showWatermark=false] - Whether to overlay a watermark div.
 * @param {() => void} props.onClose   - Called when the lightbox should close.
 */
export default function Lightbox({ photos, initialIndex, showWatermark = false, onClose }) {
  const safeInitial = Math.max(0, Math.min(initialIndex, photos.length - 1));
  const [cur, setCur] = useState(safeInitial);
  const touchStartX = useRef(null);

  const handlePrev = useCallback(
    (e) => { e?.stopPropagation(); setCur((c) => (c - 1 + photos.length) % photos.length); },
    [photos.length]
  );

  const handleNext = useCallback(
    (e) => { e?.stopPropagation(); setCur((c) => (c + 1) % photos.length); },
    [photos.length]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape")     onClose();
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft")  handlePrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, handleNext, handlePrev]);

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd   = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > SWIPE_THRESHOLD_PX) diff > 0 ? handleNext() : handlePrev();
    touchStartX.current = null;
  };

  return (
    <div
      className="lb-backdrop"
      role="presentation"
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <button className="lb-close" onClick={(e) => { e.stopPropagation(); onClose(); }}>✕</button>
      <button className="lb-arrow lb-prev" onClick={handlePrev}>←</button>

      <div className="lb-img-wrap" onClick={(e) => e.stopPropagation()}>
        <img
          key={cur}
          src={photos[cur]}
          alt=""
          className="lb-img"
          onError={(e) => { e.target.style.display = "none"; }}
        />
        {showWatermark && <div className="lb-watermark" />}
        <div className="lb-counter">{cur + 1} / {photos.length}</div>
      </div>

      <button className="lb-arrow lb-next" onClick={handleNext}>→</button>
    </div>
  );
}
